// WellPath3D.js
import React, { useRef, useMemo, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Line, Html } from '@react-three/drei';
import * as THREE from 'three';
import './WellPath3D.css';

// WellPath line component
function WellPathLine({ points, showLabels, selectedPoint, onPointClick }) {
  const lineRef = useRef();
  
  // Create line geometry
  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(points.length * 3);
    const colors = new Float32Array(points.length * 3);
    
    points.forEach((point, i) => {
      positions[i * 3] = point.dx / 10; // Scale down for better visualization
      positions[i * 3 + 1] = -point.tvd / 10; // Negative for depth downward
      positions[i * 3 + 2] = point.dy / 10;
      
      // Color gradient from surface (blue) to TD (red)
      const ratio = i / (points.length - 1);
      const color = new THREE.Color();
      color.setHSL(0.7 - ratio * 0.7, 0.8, 0.5);
      
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    });
    
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geo;
  }, [points]);

  return (
    <group>
      {/* Well path line */}
      <line ref={lineRef} geometry={geometry}>
        <lineBasicMaterial vertexColors linewidth={3} />
      </line>
      
      {/* Survey points */}
      {points.map((point, i) => (
        <mesh
          key={i}
          position={[point.dx / 10, -point.tvd / 10, point.dy / 10]}
          onClick={() => onPointClick(i)}
        >
          <sphereGeometry args={[0.5, 8, 8]} />
          <meshStandardMaterial 
            color={selectedPoint === i ? '#ff6b6b' : '#4ecdc4'}
            emissive={selectedPoint === i ? '#ff0000' : '#000000'}
            emissiveIntensity={selectedPoint === i ? 0.3 : 0}
          />
          
          {showLabels && i % Math.ceil(points.length / 10) === 0 && (
            <Html distanceFactor={10}>
              <div className="point-label">
                {point.depth.toFixed(0)}ft
              </div>
            </Html>
          )}
        </mesh>
      ))}
      
      {/* Surface marker */}
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[2, 0.2, 2]} />
        <meshStandardMaterial color="#8bc34a" />
      </mesh>
      
      {/* Surface label */}
      <Html position={[0, 2, 0]}>
        <div className="surface-label">Surface</div>
      </Html>
    </group>
  );
}

// Coordinate axes component
function CoordinateAxes() {
  return (
    <group>
      {/* X-axis (Easting) - Red */}
      <Line
        points={[[0, 0, 0], [50, 0, 0]]}
        color="red"
        lineWidth={2}
      />
      <Html position={[55, 0, 0]}>
        <div className="axis-label axis-x">X (Easting)</div>
      </Html>
      
      {/* Y-axis (Depth) - Green */}
      <Line
        points={[[0, 0, 0], [0, -50, 0]]}
        color="green"
        lineWidth={2}
      />
      <Html position={[0, -55, 0]}>
        <div className="axis-label axis-y">Y (Depth)</div>
      </Html>
      
      {/* Z-axis (Northing) - Blue */}
      <Line
        points={[[0, 0, 0], [0, 0, 50]]}
        color="blue"
        lineWidth={2}
      />
      <Html position={[0, 0, 55]}>
        <div className="axis-label axis-z">Z (Northing)</div>
      </Html>
    </group>
  );
}

// Main component
export default function WellPath3D({ data }) {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [showLabels, setShowLabels] = useState(false);
  const [showAxes, setShowAxes] = useState(true);
  const [cameraMode, setCameraMode] = useState('free'); // free, top, side, front

  // Sort and prepare data points - moved before early return
  const { sortedData, points, bounds } = useMemo(() => {
    if (!data || data.length === 0) {
      return { sortedData: [], points: [], bounds: {} };
    }
    
    const sorted = [...data].sort((a, b) => (parseFloat(a.Depth) || 0) - (parseFloat(b.Depth) || 0));
    const pointsData = sorted.map(row => ({
      depth: parseFloat(row.Depth) || 0,
      dx: parseFloat(row.DX) || 0,
      dy: parseFloat(row.DY) || 0,
      tvd: parseFloat(row.TVD) || 0,
      inclination: parseFloat(row.Inclination) || 0,
      azimuth: parseFloat(row.Azimuth) || 0
    }));

    // Calculate bounds for camera positioning
    const dxValues = pointsData.map(p => p.dx);
    const dyValues = pointsData.map(p => p.dy);
    const tvdValues = pointsData.map(p => p.tvd);
    
    const boundsData = {
      minDX: Math.min(...dxValues),
      maxDX: Math.max(...dxValues),
      minDY: Math.min(...dyValues),
      maxDY: Math.max(...dyValues),
      maxTVD: Math.max(...tvdValues)
    };

    return { sortedData: sorted, points: pointsData, bounds: boundsData };
  }, [data]);

  if (!data || data.length === 0) {
    return <div className="well-path-3d-empty">No deviation data available for 3D plotting.</div>;
  }

  // Camera position presets
  const cameraPositions = {
    free: [50, 30, 50],
    top: [0, 100, 0],
    side: [100, 0, 0],
    front: [0, 0, 100]
  };

  const selectedPointData = selectedPoint !== null ? points[selectedPoint] : null;

  return (
    <div className="well-path-3d-container">
      {/* Controls */}
      <div className="controls-panel">
        <div className="control-group">
          <label>
            <input
              type="checkbox"
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
            />
            Show Labels
          </label>
          <label>
            <input
              type="checkbox"
              checked={showAxes}
              onChange={(e) => setShowAxes(e.target.checked)}
            />
            Show Axes
          </label>
        </div>
        
        <div className="camera-controls">
          <span>Camera View:</span>
          {Object.keys(cameraPositions).map(mode => (
            <button
              key={mode}
              className={cameraMode === mode ? 'active' : ''}
              onClick={() => setCameraMode(mode)}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Point Information Panel */}
      {selectedPointData && (
        <div className="point-info-panel">
          <h4>Survey Point {selectedPoint + 1}</h4>
          <div className="point-details">
            <span><strong>Measured Depth:</strong> {selectedPointData.depth.toFixed(1)} ft</span>
            <span><strong>TVD:</strong> {selectedPointData.tvd.toFixed(1)} ft</span>
            <span><strong>Easting (DX):</strong> {selectedPointData.dx.toFixed(1)} ft</span>
            <span><strong>Northing (DY):</strong> {selectedPointData.dy.toFixed(1)} ft</span>
            <span><strong>Inclination:</strong> {selectedPointData.inclination.toFixed(1)}°</span>
            <span><strong>Azimuth:</strong> {selectedPointData.azimuth.toFixed(1)}°</span>
          </div>
          <button onClick={() => setSelectedPoint(null)}>Close</button>
        </div>
      )}

      {/* Well Statistics */}
      <div className="well-stats-3d">
        <div className="stat-item">
          <strong>Total Depth:</strong> {Math.max(...points.map(p => p.depth)).toFixed(1)} ft
        </div>
        <div className="stat-item">
          <strong>Max TVD:</strong> {Math.max(...points.map(p => p.tvd)).toFixed(1)} ft
        </div>
        <div className="stat-item">
          <strong>Horizontal Displacement:</strong> {Math.sqrt(Math.max(...points.map(p => p.dx * p.dx + p.dy * p.dy))).toFixed(1)} ft
        </div>
        <div className="stat-item">
          <strong>Survey Points:</strong> {points.length}
        </div>
      </div>

      {/* 3D Canvas */}
      <div className="canvas-container">
        <Canvas
          camera={{ position: cameraPositions[cameraMode], fov: 60 }}
          style={{ background: 'linear-gradient(to bottom, #87CEEB 0%, #E0F6FF 50%, #98FB98 100%)' }}
        >
          {/* Lighting */}
          <ambientLight intensity={0.6} />
          <directionalLight position={[10, 10, 5]} intensity={1} />
          <pointLight position={[-10, -10, -5]} intensity={0.5} />

          {/* Coordinate axes */}
          {showAxes && <CoordinateAxes />}

          {/* Well path */}
          <WellPathLine 
            points={points}
            showLabels={showLabels}
            selectedPoint={selectedPoint}
            onPointClick={setSelectedPoint}
          />

          {/* Controls */}
          <OrbitControls 
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
            autoRotate={cameraMode === 'free'}
            autoRotateSpeed={0.5}
            maxPolarAngle={Math.PI}
            minDistance={10}
            maxDistance={500}
          />
        </Canvas>
      </div>

      {/* Instructions */}
      <div className="instructions">
        <p><strong>Controls:</strong> Mouse to rotate • Scroll to zoom • Right-click to pan • Click points for details</p>
      </div>
    </div>
  );
} 